import { h } from './dom.js';

// Publishing to Scarfall (docs/2026-10-16-rfc-ugc-publish.md §5.2, §5.8). The Publish button lives in the export bar
// at the bottom left, away from the chat (fixed to the right edge): a creator who means to send a prompt must never
// publish instead. It only opens the dialog; nothing is sent until the dialog's own "Publish to Scarfall" button.
// The dialog follows the creation's publications over SSE (polling if the stream cannot be opened).

const STATUS_TEXT = {
  submitting: 'Sending…',
  processing: 'Processing…',
  pending_review: 'Waiting for review',
  published: 'Published',
  rejected: 'Rejected',
  failed: 'Failed',
  not_submitted: 'Not sent',
};
const ERROR_TEXT = {
  not_publishable: 'This outfit cannot be published.',
  publishing_unavailable: 'Publishing is not available.',
};
const NOT_AVAILABLE = 'Publishing is not available';
const UNIQUE_ID = /^[A-Za-z0-9_-]{1,64}$/;
const POLL_MS = 5000;

export function attachPublish(workspace) {
  const { api, creation, detail, viewer, exportBar } = workspace;
  const support = detail.publish ?? { supported: false, reason: NOT_AVAILABLE };
  // Off on this deployment: no button at all.
  if (!support.supported && support.reason === NOT_AVAILABLE) return;

  const button = h('button.publish', { type: 'button', disabled: !support.supported }, 'Publish');
  const note = h('span.publish-note', { role: 'status' }, support.supported ? '' : support.reason);
  exportBar.append(button, note);
  if (!support.supported) return;

  const publications = new Map();
  let dialog = null;
  let source = null;
  let poller = null;

  const open = () => [...publications.values()].some((p) => !p.final);

  function render() {
    const latest = [...publications.values()].sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
    note.textContent = latest && !latest.final ? 'Publishing…' : latest?.status === 'published' ? 'Published' : '';
    dialog?.renderList();
  }

  function upsert(p) {
    publications.set(p.id, p);
    render();
    if (!dialog && !open()) stopWatching();
  }

  async function refresh() {
    try {
      const { publications: list } = await api.publications(creation.id);
      for (const p of list) publications.set(p.id, p);
      render();
    } catch {
      // keep what we have; a 401 shows the login screen
    }
  }

  function startWatching() {
    if (source || poller) return;
    source = new EventSource(api.publicationEventsUrl(creation.id), { withCredentials: true });
    source.addEventListener('publication', (e) => upsert(JSON.parse(e.data)));
    // EventSource reconnects by itself (with Last-Event-ID) when cp-api ends the stream; if it gives up, poll.
    source.onerror = () => {
      if (source?.readyState === EventSource.CLOSED) {
        source = null;
        poller = setInterval(refresh, POLL_MS);
      }
    };
  }

  function stopWatching() {
    source?.close();
    source = null;
    clearInterval(poller);
    poller = null;
  }

  function openDialog() {
    if (dialog) return dialog.focus();
    dialog = makeDialog();
    workspace.root.append(dialog.el);
    dialog.focus();
    startWatching();
  }

  function closeDialog() {
    dialog?.el.remove();
    dialog = null;
    button.focus();
    if (!open()) stopWatching();
  }

  function makeDialog() {
    const uniqueId = h('input', {
      name: 'unique_id', required: true, maxLength: 64, autocomplete: 'off', spellcheck: false,
      placeholder: 'Node user id',
    });
    const title = h('input', {
      name: 'title', required: true, maxLength: 40,
      value: (creation.title ?? workspace.assetName ?? '').slice(0, 40),
    });
    const rarity = h('input', { name: 'rarity', type: 'number', min: 0, step: 1, placeholder: '0' });
    const error = h('div.publish-error', { role: 'alert' });
    const submit = h('button.publish-submit', { type: 'submit' }, 'Publish to Scarfall');
    const cancel = h('button.publish-cancel', { type: 'button', onclick: closeDialog }, 'Cancel');
    const list = h('ul.publish-list');
    const heading = h('h2', { id: 'publish-title' });
    const hint = h('p.publish-hint', 'Make a change before publishing: v0 is the untouched outfit.');
    let sending = false;
    // The version on screen is the one published: it changes while the dialog is open (a turn in the chat), so it is
    // read when the creator presses Publish, never kept from when the dialog opened.
    function syncVersion() {
      const version = viewer.shownVersion;
      heading.textContent = `Publish v${version} to Scarfall`;
      hint.hidden = version !== 0;
      submit.disabled = sending || version === 0;
    }

    const form = h('form.publish-form', { noValidate: true },
      h('label', 'Player ID', uniqueId),
      h('label', 'Title', title),
      h('label', 'Rarity (optional)', rarity),
      hint,
      error,
      h('div.publish-actions', cancel, submit));

    form.addEventListener('submit', async (e) => {
      // Only this form's own submit publishes; nothing else on the page reaches it.
      e.preventDefault();
      e.stopPropagation();
      error.textContent = '';
      const body = {
        version: viewer.shownVersion,
        unique_id: uniqueId.value.trim(),
        title: title.value.trim(),
        ...(rarity.value === '' ? {} : { rarity: Number(rarity.value) }),
      };
      if (!UNIQUE_ID.test(body.unique_id)) {
        error.textContent = 'Player ID: 1–64 letters, digits, _ or -.';
        return uniqueId.focus();
      }
      if (!body.title || body.title.length > 40) {
        error.textContent = 'Title: 1–40 characters.';
        return title.focus();
      }
      if (body.rarity !== undefined && (!Number.isInteger(body.rarity) || body.rarity < 0)) {
        error.textContent = 'Rarity: a whole number, 0 or more.';
        return rarity.focus();
      }
      sending = true;
      syncVersion();
      submit.textContent = 'Publishing…';
      try {
        upsert(await api.publish(creation.id, body));
        startWatching();
      } catch (err) {
        if (err.code === 'publish_refused') {
          if (err.details.publication) upsert(err.details.publication);
          error.textContent = `Scarfall refused it: ${err.message}${err.details.scarfall_code ? ` (${err.details.scarfall_code})` : ''}`;
        } else {
          error.textContent = ERROR_TEXT[err.code] ?? err.message;
        }
      } finally {
        sending = false;
        syncVersion();
        submit.textContent = 'Publish to Scarfall';
      }
    });

    const el = h('section.publish-dialog', { role: 'dialog', 'aria-labelledby': 'publish-title' },
      heading, form, h('h3', 'Publications'), list);
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeDialog();
      }
    });

    syncVersion();
    return {
      el,
      syncVersion,
      focus: () => uniqueId.focus(),
      renderList() {
        const rows = [...publications.values()].sort((a, b) => b.created_at.localeCompare(a.created_at));
        list.replaceChildren(...(rows.length ? rows.map((p) => h(`li.publish-item.${p.status}`,
          h('div', h('strong', p.title), ` · v${p.version} · ${p.unique_id}`),
          h('div.publish-status', STATUS_TEXT[p.status] ?? p.status,
            p.asset_id ? ` · asset ${p.asset_id}` : '', p.reason ? ` · ${p.reason}` : ''))) : [h('li.publish-empty', 'None yet.')]));
      },
    };
  }

  button.addEventListener('click', openDialog);
  workspace.onShow.push(() => dialog?.syncVersion());
  // Pick up publications still in progress (the button shows "Publishing…" until they finish).
  refresh().then(() => {
    if (open()) startWatching();
  });
}
